
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import NavBar from "@/components/NavBar";
import { Loader2 } from "lucide-react";
import ProjectSelectorHeader from "@/components/project-selector/ProjectSelectorHeader";
import ProjectFilters from "@/components/project-selector/ProjectFilters";
import ProjectGrid from "@/components/project-selector/ProjectGrid";
import { filterProjects, sortProjects } from "@/components/project-selector/ProjectSelectorUtils";

export default function ProjectSelector() {
  const { projects, setCurrentProject, isLoading, error } = useProject();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);
  
  // Filter and sort projects
  const filteredProjects = sortProjects(
    filterProjects(projects, searchTerm, stageFilter, statusFilter, riskFilter),
    sortBy
  );
  
  const handleProjectSelect = (project: any) => {
    console.log("Selecting project:", project.name);
    setCurrentProject(project);
    navigate("/dashboard");
  };

  // Set page title to indicate this is the main portfolio page
  useEffect(() => {
    document.title = "Project Portfolio - Stratafold";
    return () => {
      document.title = "Stratafold";
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-1 container py-4 sm:py-6 px-4 sm:px-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
            <span className="ml-2 text-sm sm:text-base">Loading projects...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-1 container py-4 sm:py-6 px-4 sm:px-6">
          <div className="text-center py-12">
            <div className="bg-red-50 rounded-lg p-6 sm:p-8 max-w-md mx-auto">
              <h3 className="text-lg sm:text-xl font-medium mb-2 text-red-900">Error Loading Projects</h3>
              <p className="text-sm sm:text-base text-red-700 mb-4 sm:mb-6">{error.message}</p>
              <button 
                className="text-sm sm:text-base px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      
      <main className="flex-1 container py-4 sm:py-6 px-4 sm:px-6 max-w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Project Portfolio</h1>
          <p className="text-muted-foreground">
            Select a project to view its dashboard and access all tools and features.
          </p>
        </div>
        
        <ProjectFilters 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          stageFilter={stageFilter}
          setStageFilter={setStageFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
        
        <ProjectGrid 
          projects={filteredProjects}
          onProjectSelect={handleProjectSelect}
        />
      </main>
    </div>
  );
}
